import type { ReactNode } from "react";
import { SiteShell, ImagePlaceholder } from "./SiteShell";
import { PageHero, Section, FeatureGrid, CTA, Breadcrumbs, FAQGrid, Prose, type HeroVariant } from "./Sections";
import { Link } from "@tanstack/react-router";

export type ServicePageProps = {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  bullets: string[];
  features: { title: string; body: string }[];
  faq: { q: string; a: string }[];
  extra?: ReactNode;
  heroVariant?: HeroVariant;
  accentColor?: string;
  /** Custom hero image URL. */
  heroImageSrc?: string;
  /** Long-form intro/section under the hero. */
  longIntro?: ReactNode;
  /** Free-form rich content blocks. */
  body?: ReactNode;
  /** Step-by-step process. */
  process?: { title: string; body: string }[];
  /** Optional stat cards. */
  stats?: { value: string; label: string }[];
  /** Optional related links section. */
  related?: { to: string; title: string; body: string }[];
};

export function ServicePage({
  slug, eyebrow, title, intro, bullets, features, faq, extra,
  heroVariant = "split", accentColor, heroImageSrc, longIntro, body, process, stats, related,
}: ServicePageProps) {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Ydelser", to: "/ydelser" }, { label: title }]} />
      <PageHero
        variant={heroVariant}
        accentColor={accentColor}
        eyebrow={eyebrow}
        title={title}
        intro={intro}
        imageLabel={`Billede: ${title}`}
        imageSrc={heroImageSrc}
        stats={stats}
      />

      {longIntro && (
        <Section className="!pt-14">
          <Prose>{longIntro}</Prose>
        </Section>
      )}

      <Section className={longIntro ? "!pt-0" : ""}>
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="text-3xl">Hvad får du?</h2>
            <ul className="mt-5 space-y-3 text-[var(--ink)]/85">
              {bullets.map((b) => (
                <li key={b} className="flex gap-3">
                  <span aria-hidden className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <ImagePlaceholder label={`Foto: ${title}`} ratio="aspect-[5/4]" />
        </div>
      </Section>

      <Section className="!pt-0">
        <FeatureGrid items={features} />
      </Section>

      {body && (
        <Section className="!pt-0">
          <Prose>{body}</Prose>
        </Section>
      )}

      {process && process.length > 0 && (
        <Section className="!pt-0">
          <h2 className="text-3xl">Sådan forløber opgaven</h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-4">
            {process.map((s, i) => (
              <li key={s.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 grid h-9 w-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                <h3 className="font-semibold text-[var(--ink)]">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {extra}

      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål</h2>
        <div className="mt-8"><FAQGrid items={faq} /></div>
      </Section>

      {related && related.length > 0 && (
        <Section className="!pt-0">
          <h2 className="text-3xl">Relaterede ydelser</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.to} to={r.to} className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/50 hover:shadow-[var(--shadow-soft)]">
                <h3 className="font-semibold text-[var(--ink)] group-hover:text-[var(--brand)]">{r.title} →</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <CTA />
      <div className="h-10" />
    </SiteShell>
  );
}

export function serviceHead(slug: string, title: string, description: string) {
  return {
    meta: [
      { title: `${title} | Elektriker-Elektriker` },
      { name: "description", content: description },
      { property: "og:title", content: `${title} | Elektriker-Elektriker` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `/${slug}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `https://elektriker-elektriker.dk/${slug}` }],
  };
}
